/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * cw-core Theme - Custom JavaScript. The theme is easily extensible through JavaScript
 * events and custom elements (https://developers.google.com/web/fundamentals/web-components/customelements).
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  if (window.cwcWatchPurchaseLeadTrackingInstalled) {
    return;
  }

  if (!/(^|\/)pages\/uhren-ankauf\/?$/.test(window.location.pathname)) {
    return;
  }

  window.cwcWatchPurchaseLeadTrackingInstalled = true;

  var ALLOWED_ORIGIN = 'https://formbuilder.hulkapps.com';
  var FORM_ID = 'Z3I3t3m344jsxhh3wkw9_g';
  var CONVERSION_SEND_TO = 'AW-1000561475/0RH0CPrOy44cEMO2jd0D';
  var fired = false;

  function parseMessage(data) {
    if (!data) {
      return null;
    }

    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (error) {
        return null;
      }
    }

    if (typeof data === 'object') {
      return data;
    }

    return null;
  }

  function hasMarketingConsent() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;

    if (!privacy || typeof privacy.currentVisitorConsent !== 'function') {
      return false;
    }

    var consent = privacy.currentVisitorConsent();

    return consent && consent.marketing === 'yes';
  }

  function normalizeEmail(email) {
    return normalizeText(email);
  }

  function normalizeText(value) {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim().toLowerCase();
  }

  function normalizePhone(phoneNumber) {
    if (typeof phoneNumber !== 'string') {
      return '';
    }

    return phoneNumber.trim();
  }

  function buildUserData(payload) {
    var userData = {};
    var email = normalizeEmail(payload.email);
    var phoneNumber = normalizePhone(payload.phoneNumber || payload.phone_number);
    var firstName = normalizeText(payload.firstName || payload.first_name);
    var lastName = normalizeText(payload.lastName || payload.last_name);

    if (email) {
      userData.email = email;
    }

    if (phoneNumber) {
      userData.phone_number = phoneNumber;
    }

    if (firstName || lastName) {
      userData.address = {};

      if (firstName) {
        userData.address.first_name = firstName;
      }

      if (lastName) {
        userData.address.last_name = lastName;
      }
    }

    return userData;
  }

  function hasUserData(userData) {
    return Boolean(userData.email || userData.phone_number || userData.address);
  }

  function waitForGtag(callback, attempt) {
    attempt = attempt || 0;

    if (typeof window.gtag === 'function') {
      callback(window.gtag);
      return;
    }

    if (attempt >= 40) {
      return;
    }

    window.setTimeout(function () {
      waitForGtag(callback, attempt + 1);
    }, 100);
  }

  function fireConversion(payload) {
    if (fired || !hasMarketingConsent()) {
      return;
    }

    fired = true;

    waitForGtag(function (gtag) {
      var userData = buildUserData(payload);

      if (hasUserData(userData)) {
        gtag('set', 'user_data', userData);
      }

      gtag('event', 'conversion', {
        send_to: CONVERSION_SEND_TO,
        event_category: 'Uhren Ankauf form submission',
        event_action: 'Uhren Ankauf successful form submission',
        event_label: window.location.href,
        form_id: FORM_ID
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'cwc_watch_purchase_lead_parent_fired',
        form_id: FORM_ID
      });
    });
  }

  window.addEventListener('message', function (event) {
    if (event.origin !== ALLOWED_ORIGIN) {
      return;
    }

    var payload = parseMessage(event.data);

    if (!payload || payload.type !== 'cwc_watch_purchase_lead' || payload.formId !== FORM_ID) {
      return;
    }

    fireConversion(payload);
  });
})();

(function () {
  if (window.cwcWorkshopServiceLeadTrackingInstalled) {
    return;
  }

  if (!/(^|\/)pages\/werkstattservice\/?$/.test(window.location.pathname)) {
    return;
  }

  window.cwcWorkshopServiceLeadTrackingInstalled = true;

  var ALLOWED_ORIGIN = 'https://formbuilder.hulkapps.com';
  var FORM_ID = 'yLmBSU9KgTgewLaPTAu6MA';
  var CONVERSION_SEND_TO = 'AW-1000561475/TE3DCNXC1ascEMO2jd0D';
  var fired = false;
  var standardFallbackTimer = null;

  function parseMessage(data) {
    if (!data) {
      return null;
    }

    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (error) {
        return null;
      }
    }

    if (typeof data === 'object') {
      return data;
    }

    return null;
  }

  function hasMarketingConsent() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;

    if (!privacy || typeof privacy.currentVisitorConsent !== 'function') {
      return false;
    }

    var consent = privacy.currentVisitorConsent();

    return consent && consent.marketing === 'yes';
  }

  function normalizeEmail(email) {
    return normalizeText(email);
  }

  function normalizeText(value) {
    if (typeof value !== 'string') {
      return '';
    }

    return value.trim().toLowerCase();
  }

  function normalizePhone(phoneNumber) {
    if (typeof phoneNumber !== 'string') {
      return '';
    }

    return phoneNumber.trim();
  }

  function buildUserData(payload) {
    var userData = {};
    var email = normalizeEmail(payload.email);
    var phoneNumber = normalizePhone(payload.phoneNumber || payload.phone_number);
    var firstName = normalizeText(payload.firstName || payload.first_name);
    var lastName = normalizeText(payload.lastName || payload.last_name);

    if (email) {
      userData.email = email;
    }

    if (phoneNumber) {
      userData.phone_number = phoneNumber;
    }

    if (firstName || lastName) {
      userData.address = {};

      if (firstName) {
        userData.address.first_name = firstName;
      }

      if (lastName) {
        userData.address.last_name = lastName;
      }
    }

    return userData;
  }

  function hasUserData(userData) {
    return Boolean(userData.email || userData.phone_number || userData.address);
  }

  function waitForGtag(callback, attempt) {
    attempt = attempt || 0;

    if (typeof window.gtag === 'function') {
      callback(window.gtag);
      return;
    }

    if (attempt >= 40) {
      return;
    }

    window.setTimeout(function () {
      waitForGtag(callback, attempt + 1);
    }, 100);
  }

  function clearStandardFallback() {
    if (!standardFallbackTimer) {
      return;
    }

    window.clearTimeout(standardFallbackTimer);
    standardFallbackTimer = null;
  }

  function fireConversion(payload) {
    if (fired || !hasMarketingConsent()) {
      return;
    }

    fired = true;
    clearStandardFallback();

    waitForGtag(function (gtag) {
      var userData = buildUserData(payload);

      if (hasUserData(userData)) {
        gtag('set', 'user_data', userData);
      }

      gtag('event', 'conversion', {
        send_to: CONVERSION_SEND_TO,
        event_category: 'Werkstattservice form submission',
        event_action: 'Werkstattservice successful form submission',
        event_label: window.location.href,
        form_id: FORM_ID
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'cwc_workshop_service_lead_parent_fired',
        form_id: FORM_ID
      });
    });
  }

  function buildStandardSubmissionPayload(payload) {
    var data = payload && payload.payload;

    if (!data || data.form_id !== FORM_ID) {
      return null;
    }

    return {
      email: data.email || ''
    };
  }

  function scheduleStandardFallback(payload) {
    clearStandardFallback();

    standardFallbackTimer = window.setTimeout(function () {
      standardFallbackTimer = null;
      fireConversion(payload);
    }, 1500);
  }

  window.addEventListener('message', function (event) {
    if (event.origin !== ALLOWED_ORIGIN) {
      return;
    }

    var payload = parseMessage(event.data);

    if (!payload) {
      return;
    }

    if (payload.type === 'form_submitted') {
      var standardPayload = buildStandardSubmissionPayload(payload);

      if (standardPayload) {
        scheduleStandardFallback(standardPayload);
      }

      return;
    }

    if (payload.type === 'cwc_workshop_service_lead' && payload.formId === FORM_ID) {
      fireConversion(payload);
    }
  });
})();
